import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Book, Users, HandHeart, Stethoscope, ArrowRight, Star, Heart, ArrowRightCircle } from 'lucide-react';
import UpcomingEvents from '../components/Calendar/UpcomingEvents';
import { eventUtils } from '../lib/eventUtils';
import MarketplaceProduct from '../components/MarketplaceProduct';
import FeaturedMarketplace from '../components/FeaturedMarketplace';
import FeaturedPhotos from '../components/FeaturedPhotos';

const AnimatedFeatureCard = ({ icon: Icon, title, description, index }) => (
    <div
        className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500
                   transform hover:-translate-y-2 group relative overflow-hidden"
        style={{ animationDelay: `${index * 150}ms` }}
    >
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-purple-50
                       rounded-bl-full -z-10 transition-all duration-500 group-hover:scale-110" />
        <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-purple-50 rounded-full -z-10
                       transition-all duration-500 group-hover:scale-110" />

        <div className="relative z-10">
            <div className="flex items-center justify-center w-16 h-16 mb-6
                          bg-gradient-to-br from-purple-600 to-purple-500
                          rounded-2xl transform rotate-3 group-hover:rotate-6
                          transition-all duration-500 group-hover:scale-110">
                <Icon className="h-8 w-8 text-white transform -rotate-3
                               group-hover:-rotate-6 transition-all duration-500" />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-gray-900 group-hover:text-purple-700
                          transition-colors duration-300">
                {title}
            </h3>
            <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300
                         leading-relaxed">
                {description}
            </p>
        </div>
    </div>
);

const ImpactCard = ({ text }) => (
    <div className="flex items-center space-x-4 group bg-white p-5 rounded-xl shadow-md
                    hover:shadow-lg transition-all duration-300 transform hover:-translate-x-1">
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl
                       bg-gradient-to-br from-purple-600 to-purple-500 transform rotate-3
                       group-hover:rotate-6 transition-all duration-300">
            <Star className="h-6 w-6 text-white transform -rotate-3
                           group-hover:-rotate-6 transition-all duration-300" />
        </div>
        <span className="text-gray-800 group-hover:text-purple-700 transition-colors duration-300
                        font-medium">
            {text}
        </span>
    </div>
);

const ProgramHighlight = ({ image, title, description, content }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div className="relative rounded-3xl overflow-hidden shadow-xl h-96 lg:h-[600px]">
            <img
                src={image}
                alt={title}
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="text-white space-y-4">
                    <h3 className="text-3xl font-bold">{title}</h3>
                    <p className="text-lg text-gray-100 leading-relaxed max-w-xl">{description}</p>
                </div>
            </div>
        </div>
        <div className="space-y-6 lg:p-8">
            <h3 className="text-4xl font-bold text-gray-900">{title}</h3>
            <div className="prose prose-lg prose-gray max-w-none">
                {content}
            </div>
        </div>
    </div>
);

const HomePage = () => {
    const [events, setEvents] = React.useState([]);
    const [featuredProducts, setFeaturedProducts] = React.useState([]);
    const [showAllProducts, setShowAllProducts] = React.useState(false);

    React.useEffect(() => {
        eventUtils.getEvents()
            .then(setEvents)
            .catch(console.error);
        fetchFeaturedProducts();
    }, []);

    const fetchFeaturedProducts = async () => {
        try {
            const response = await fetch('/api/marketplace');
            const data = await response.json();
            // Get 3 random products
            const shuffled = data.sort(() => 0.5 - Math.random());
            setFeaturedProducts(shuffled.slice(0, 3));
        } catch (error) {
            console.error('Error fetching featured products:', error);
        }
    };

    const features = [
        {
            icon: HandHeart,
            title: "Community Support",
            description: "Providing essential monthly support through food packages, clothing, and counseling to families in need."
        },
        {
            icon: Stethoscope,
            title: "Cancer Support",
            description: "'No One Fights Alone' - Offering holistic support to cancer survivors through ABCSC with financial and emotional assistance."
        },
        {
            icon: Book,
            title: "Youth Development",
            description: "Empowering young entrepreneurs aged 8-17 through ABYETS with skills training in culinary arts, fashion, and more."
        },
        {
            icon: Users,
            title: "Family Services",
            description: "Building stronger families through counseling, spiritual guidance, and relationship support services."
        }
    ];

    const abcscContent = (
        <>
            <p className="mb-4">
                Established in June 2020 and officially launched in June 2021, ABCSC provides holistic
                support to people living after a diagnosis of all types of cancers. The program was
                founded by Emelda Benjamin, a director of CCM, Registered Nurse, Certified Midwife
                and breast cancer survivor, whose personal experience led to the formation of this
                vital support group.
            </p>
            <p className="mb-4">
                Currently supporting 40 members affected by various types of cancer including Breast,
                Head & Neck, Colon, Cervical, Multiple Myeloma, Skin and Lymphoma cancers. The program
                provides comprehensive financial and psychosocial support for cancer survivors, both
                members and non-members, and their families.
            </p>
            <p>
                ABCSC organizes annual Cancer Survivors Day celebrations in June, including media
                outreach, care package distribution, and survivors' retreat. The motto "No One Fights
                Alone" embodies our commitment to providing continuous support and creating a strong
                community for those affected by cancer.
            </p>
        </>
    );

    const abyetsContent = (
        <>
            <p className="mb-4">
                Launched in April 2022, ABYETS provides comprehensive support and mentorship for young
                entrepreneurs ages 8-17. The program offers specialized training in diverse fields
                including culinary arts, farming, fashion & graphic design, jewelry & soap making,
                sports coaching, tutoring, and entertainment.
            </p>
            <p className="mb-4">
                Through ABYETS, we develop young businesspersons through hands-on experience, annual
                trade shows, and a local entrepreneurial gallery. The program creates valuable networking
                opportunities and provides ongoing mentorship to nurture the next generation of
                business leaders.
            </p>
            <p>
                Our mission is to invest in the future of our nation, gearing their mindset to becoming
                self-sustaining businesspersons while honing their marketing skills. We focus on building
                self-sustaining skills while fostering empathy and independence in young entrepreneurs,
                preparing them for successful futures in their chosen fields.
            </p>
        </>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section - Keep purple effects */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={require("../assets/images/charity1.jpg")}
                        alt="Community Charitable Ministry"
                        className="w-full h-full object-cover object-center transform scale-105
                                 motion-safe:animate-[ken-burns_20s_ease-in-out_infinite_alternate]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-purple-900/40 to-black/80" />
                </div>

                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/10
                                  rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-300/10
                                  rounded-full blur-3xl animate-pulse delay-1000" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="max-w-3xl mx-auto text-center space-y-8">
                        <div className="inline-flex items-center px-4 py-2 rounded-full
                                      bg-white/10 backdrop-blur-sm border border-white/20">
                            <Heart className="h-5 w-5 text-purple-300 mr-2" />
                            <span className="text-purple-100 font-medium">Serving Since 2008</span>
                        </div>
                        <div className="space-y-6">
                            <h1 className="text-6xl md:text-8xl font-bold text-white
                                       tracking-tight leading-none">
                                Building a Better
                                <span className="block mt-2 bg-gradient-to-r from-purple-200 to-white
                                             text-transparent bg-clip-text">
                                    Community
                                </span>
                            </h1>
                            <p className="text-xl md:text-2xl text-purple-100 max-w-2xl mx-auto
                                      leading-relaxed font-light">
                                Dedicated to transforming lives through compassion, service, and
                                unwavering community support.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-center gap-6 pt-8">
                            <Link
                                to="/support"
                                className="inline-flex items-center px-8 py-4 rounded-full
                                         bg-white text-purple-900 font-semibold text-lg
                                         hover:bg-purple-50 transition-all duration-300
                                         transform hover:-translate-y-1 shadow-lg hover:shadow-xl
                                         group"
                            >
                                Support Our Mission
                                <ArrowRightCircle className="ml-2 h-5 w-5 group-hover:rotate-90
                                                       transition-transform duration-500" />
                            </Link>
                            <Link
                                to="/about"
                                className="inline-flex items-center px-6 py-3 rounded-full
                                         bg-white text-purple-600 hover:bg-purple-50
                                         transition duration-300 shadow-lg hover:shadow-xl
                                         transform hover:-translate-y-0.5"
                            >
                                Explore Services
                                <ArrowRightCircle className="ml-2 h-5 w-5" />
                            </Link>
                        </div>

                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2
                                      animate-bounce">
                            <div className="w-[2px] h-16 bg-gradient-to-b from-white to-transparent
                                          rounded-full" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Photos Section - Added after hero */}
            <FeaturedPhotos />

            {/* FeaturedMarketplace Section - Updated styling */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-6 sm:mb-10 flex items-end justify-between gap-4">
                        <div>
                            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-gray-900">Marketplace Highlights</h2>
                            <p className="mt-1 text-sm sm:text-lg text-gray-600">Unique items from our community entrepreneurs</p>
                        </div>
                    </div>
                    <FeaturedMarketplace />
                </div>
            </section>

            {/* Programs Section - Simplified */}
            <section className="py-24 bg-gradient-to-b from-white to-gray-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Programs</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Making a lasting impact through specialized initiatives that address
                            our community's most pressing needs
                        </p>
                    </div>

                    <div className="space-y-32">
                        <ProgramHighlight
                            image="/images/cancersupport.jpg"
                            title="Cancer Support Community (ABCSC)"
                            description="Providing holistic support to cancer survivors and their families since 2020. A community where No One Fights Alone."
                            content={abcscContent}
                        />

                        <ProgramHighlight
                            image="/images/abyets.jpg"
                            title="Youth Entrepreneurship (ABYETS)"
                            description="Empowering young entrepreneurs ages 8-17 with skills in culinary arts, fashion design, and business development."
                            content={abyetsContent}
                        />
                    </div>
                </div>
            </section>

            {/* Features Section - Keep purple effects */}
            <section className="py-24 bg-gradient-to-b from-purple-50/50 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 space-y-4">
                        <h2 className="text-4xl font-bold text-gray-900">How We Help</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Our comprehensive approach to community support focuses on these key areas
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <AnimatedFeatureCard key={index} {...feature} index={index} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Section - Keep purple effects */}
            <section className="relative py-24 overflow-hidden bg-purple-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                            <img
                                src={require("../assets/images/charity2.jpg")}
                                alt="Our Community Impact"
                                className="w-full h-[500px] object-cover transform transition-transform
                                         duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90
                                          via-purple-900/50 to-transparent opacity-80
                                          group-hover:opacity-70 transition-opacity duration-500" />
                            <div className="absolute bottom-0 left-0 right-0 p-8">
                                <div className="text-white space-y-4">
                                    <h3 className="text-3xl font-bold">Real Stories, Real Impact</h3>
                                    <p className="text-lg text-gray-100">
                                        See how our programs are transforming lives and building
                                        stronger communities every day.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-8">
                            <div className="space-y-4">
                                <h2 className="text-4xl font-bold text-gray-900">Our Impact</h2>
                                <p className="text-xl text-gray-600 leading-relaxed">
                                    Through dedicated community efforts and your support,
                                    we continue to create lasting positive change in our community.
                                </p>
                            </div>
                            <div className="space-y-6">
                                {[
                                    "Monthly food and clothing distribution events serving 200+ families",
                                    "Comprehensive cancer support for 40+ survivors and their families",
                                    "Youth entrepreneurship program empowering 100+ young minds",
                                    "Community wellness initiatives reaching thousands annually"
                                ].map((item, index) => (
                                    <ImpactCard key={index} text={item} />
                                ))}
                            </div>
                            <div className="pt-4">
                                <Link
                                    to="/news"
                                    className="inline-flex items-center px-8 py-4 rounded-full
                                             bg-purple-600 text-white font-semibold
                                             hover:bg-purple-700 transition-all duration-300
                                             group transform hover:-translate-y-1 shadow-lg
                                             hover:shadow-xl"
                                >
                                    Explore Our Impact
                                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1
                                                       transition-transform duration-300" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="relative py-32 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20
                                  rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-300/20
                                  rounded-full blur-3xl animate-pulse delay-1000" />
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                    <div className="max-w-3xl mx-auto space-y-8">
                        <h2 className="text-5xl font-bold text-white leading-tight">
                            Join Us in Making a
                            <span className="block mt-2 bg-gradient-to-r from-purple-200 to-white
                                         text-transparent bg-clip-text">
                                Lasting Difference
                            </span>
                        </h2>
                        <p className="text-xl text-gray-100 leading-relaxed">
                            Your support empowers us to continue our mission of serving and
                            uplifting our community. Every contribution creates ripples of
                            positive change that transform lives for generations to come.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-6 pt-8">
                            <Link
                                to="/support"
                                className="w-full sm:w-auto inline-flex items-center justify-center
                                         px-8 py-4 rounded-full bg-white text-purple-900
                                         font-semibold text-lg hover:bg-purple-50
                                         transition-all duration-300 transform
                                         hover:-translate-y-1 shadow-lg hover:shadow-xl
                                         group"
                            >
                                Make a Donation
                                <ArrowRightCircle className="ml-2 h-5 w-5 group-hover:rotate-90
                                                       transition-transform duration-500" />
                            </Link>
                            <Link
                                to="/contact"
                                className="w-full sm:w-auto inline-flex items-center justify-center
                                         px-8 py-4 rounded-full border-2 border-white/20
                                         text-white font-semibold text-lg hover:bg-white/10
                                         transition-all duration-300 backdrop-blur-sm
                                         group transform hover:-translate-y-1"
                            >
                                Volunteer With Us
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2
                                                   transition-transform duration-300" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Move the events section to the bottom */}
            <section className="py-20 bg-gradient-to-b from-white to-purple-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Community Events
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Stay connected with our community. Join us at our upcoming events and activities.
                        </p>
                    </div>
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 opacity-50 rounded-3xl"></div>
                        <div className="relative bg-white/80 backdrop-blur-sm shadow-xl rounded-3xl p-8">
                            <UpcomingEvents events={events} />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
